'use client';

// Presentational pieces of the shell: the password gate, the account panel, and
// the wiki tree in the sidebar. Split out of the one component that used to be
// this whole app, unchanged — see app/console.tsx for what the split was for.

import { Fragment, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { commitAge } from '@/lib/sessions';
import { childNamed, isProject } from '@/lib/project';
import { byProject } from '@/lib/sessions';
import type { TreeNode } from '@/lib/wiki';
import type { Workspace } from '@/lib/runner';
import { getServerSnapshot as getThemeServerSnapshot, getSnapshot as getThemeSnapshot, setTheme, subscribe as subscribeTheme, type Theme } from '@/lib/theme-store';
import type { Me } from './console';
import { Mark } from './boot';

/**
 * Password prompt. The candidate password is verified by making a real
 * authenticated request rather than trusted locally — so a wrong one never
 * reaches localStorage in the first place.
 */
export function Gate({
  notice,
  onUnlock,
  arriving = false,
}: {
  notice: string | null;
  onUnlock: (token: string) => void;
  arriving?: boolean;
}) {
  const [value, setValue] = useState('');
  const [checking, setChecking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const candidate = value.trim();
    if (!candidate || checking) return;

    setChecking(true);
    setProblem(null);
    try {
      const res = await fetch('/api/workspaces', {
        headers: { Authorization: `Bearer ${candidate}` },
      });
      if (res.status === 401) {
        setProblem('Wrong password.');
        setValue('');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setProblem(body.error ?? `Server returned ${res.status}.`);
        return;
      }
      onUnlock(candidate);
    } catch {
      setProblem('Could not reach the server.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className={`gate${arriving ? ' is-arriving' : ''}`}>
      <form className="gate-card" onSubmit={submit}>
        {/* The same mark the veil in ./boot was holding, at a quarter of the
            size and standing still. Coming out of the loading screen it is the
            one thing on both sides of the dissolve, which is what makes the card
            read as growing around it rather than replacing it. */}
        <div className="wordmark">
          <Mark />
          console
        </div>
        <p className="gate-copy">This machine&rsquo;s Claude session. Enter the password.</p>
        <input
          type="password"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="Password"
          autoFocus
          autoComplete="current-password"
          disabled={checking}
        />
        <button type="submit" disabled={checking || !value.trim()}>
          {checking ? 'Checking…' : 'Unlock'}
        </button>
        {(problem ?? notice) && <div className="gate-problem">{problem ?? notice}</div>}
      </form>
    </div>
  );
}

const THEME_OPTIONS: { value: Theme; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'system', label: 'System' },
  { value: 'dark', label: 'Dark' },
];

/**
 * Account settings.
 *
 * A native <dialog> driven by showModal(), which is doing real work here rather
 * than being a tag choice: it traps focus, closes on Escape, marks everything
 * behind it inert, and hands focus back to whatever opened it. All of that would
 * otherwise be hand-rolled, and hand-rolled focus traps are usually subtly wrong.
 *
 * The copy is deliberately blunt about what this is. There is one shared
 * password, the runner cannot tell two browsers apart, and every session runs as
 * the same host user — an account panel that implied otherwise would be the
 * interface lying about its own security model.
 */
export function AccountModal({
  me,
  open,
  onClose,
  onLogOut,
}: {
  me: Me | null;
  open: boolean;
  onClose: () => void;
  onLogOut: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const theme = useSyncExternalStore(subscribeTheme, getThemeSnapshot, getThemeServerSnapshot);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="modal"
      // Fires for Escape too, so this is the single place closing is reported
      // back up rather than one handler per way out.
      onClose={onClose}
      // A click on a modal <dialog> that lands on the element itself is a click
      // on the backdrop — the panel inside is what catches anything else.
      onClick={e => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="modal-panel">
        <div className="modal-head">
          <span className="avatar avatar-lg" aria-hidden="true">
            {(me?.label ?? '·').slice(0, 1)}
          </span>
          <div>
            <h2 className="modal-title">{me?.label ?? 'Signing in…'}</h2>
            <p className="modal-sub">Signed in on this device</p>
          </div>
        </div>

        {/* The note that was here said the opposite — one shared password, no
            user account, everyone reaching the same thing. That stopped being
            true when the console got a second user, and a panel that
            understates access is worse than one that says nothing. What is
            still true is the part worth keeping: the password is the whole
            credential, and holding it is holding the account. */}
        <p className="modal-note">
          {me
            ? `Your password is the whole credential for ${me.label}: it decides which machine
               account answers, which repos exist, and whose conversations are listed. Anyone
               holding it is you. It is stored in this browser until you log out.`
            : `Your password is the whole credential — it decides which machine account answers
               and which repos exist. It is stored in this browser until you log out.`}
        </p>

        <div className="theme-field">
          <span className="section-label">Appearance</span>
          <div className="theme-picker" role="radiogroup" aria-label="Colour scheme">
            {THEME_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={theme === opt.value}
                className={`theme-option${theme === opt.value ? ' is-selected' : ''}`}
                onClick={() => setTheme(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn-quiet" onClick={onClose}>
            Close
          </button>
          <button className="btn-danger" onClick={onLogOut}>
            Log out
          </button>
        </div>
      </div>
    </dialog>
  );
}


/**
 * The tree that is coming, drawn in the shape it will arrive in.
 *
 * "Loading…" is a word where a list is about to be, so the drawer stayed empty
 * and then jumped. This holds the space at the right size: two documents, a
 * folder, and what is inside it — which is the shape of every wiki this is
 * pointed at, and near enough the shape of any other that it will not lurch
 * when the real rows land.
 *
 * The widths are fixed rather than random. A random width is a different string
 * on the server than on the client, and React calls that a hydration mismatch.
 */
const TREE_SKELETON: string[] = ['38%', '30%', '46%', '68%', '52%', '60%', '44%', '56%'];

export function WikiTreeSkeleton() {
  return (
    <>
      {/* The skeleton is scenery; this is the part a screen reader needs. */}
      <span className="sr-only" role="status">
        Loading the wiki…
      </span>
      {TREE_SKELETON.map((width, i) => (
        <div key={i} className="wiki-row is-skeleton" aria-hidden="true">
          <span className="skeleton" style={{ width }} />
        </div>
      ))}
    </>
  );
}


/**
 * One level of the wiki tree, recursing into whichever directories are open.
 *
 * Every row sits at the same padding regardless of depth — a project inside
 * the permanent Projects section reads at the same indent as Projects itself
 * or a loose root document, rather than stepping in per level. `depth` still
 * drives the recursion and the permanent-section check below; it no longer
 * touches layout, which used to be inline for exactly the reason a per-level
 * rule would have been wrong — the tree is however deep the wiki is. Nothing
 * currently nests past one level, so the difference is dormant rather than
 * visible, but the moment it isn't, it stays flat on purpose rather than by
 * omission.
 *
 * Names truncate with an ellipsis rather than wrapping, which loses the tail of
 * `a-night-at-ashworth-manor` in this width. That is the accepted trade for now:
 * the start of a name is enough to tell these apart, and the full one is on the
 * row's title. Worth revisiting from use rather than from first principles.
 */
export function WikiRows({
  nodes,
  depth,
  expanded,
  onToggle,
  selected,
  onSelect,
  fixtures,
}: {
  nodes: TreeNode[];
  depth: number;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  selected: string | null;
  onSelect: (node: TreeNode) => void;
  /** Project directories to leave out — see `Project.fixture`. */
  fixtures: Set<string>;
}) {
  return (
    <>
      {nodes
        // Not `hidden` in lib/wiki.ts, which is where every other "don't show
        // this" lives: that filter runs while the tree is built, and the project
        // page reads a project's parts off that same tree. Hiding a fixture
        // there would stop it being a project page at all, which is the one
        // thing it exists to be.
        .filter(node => !fixtures.has(node.path))
        .map(node =>
        // A project directory is a leaf here, not a folder. Its parts —
        // `project.md`, `state.md`, `notes/` — are one thing to a reader, and
        // showing them as three rows called "Project", "State" and "Notes" under
        // every project would be the filesystem leaking back into a shelf that
        // spent a commit learning not to look like one.
        node.type === 'dir' && !isProject(node) ? (
          depth === 0 ? (
            // A permanent section rather than a disclosure. `expanded` used to
            // seed this open by default and nothing in practice ever closed
            // it — see the comment where it's seeded — so the toggle was
            // spending a caret and a click on a state that was already
            // decided. Keyed on depth rather than on the name "projects": any
            // directory that survives to the wiki root gets this, the same
            // way `isProject` reads what a directory contains rather than
            // what it's called.
            <Fragment key={node.path}>
              <div className="wiki-section-label section-label">{node.name}</div>
              <WikiRows
                nodes={node.children}
                depth={depth + 1}
                expanded={expanded}
                onToggle={onToggle}
                selected={selected}
                onSelect={onSelect}
                fixtures={fixtures}
              />
            </Fragment>
          ) : (
            <Fragment key={node.path}>
              <button
                className="wiki-row is-dir"
                onClick={() => onToggle(node.path)}
                aria-expanded={expanded.has(node.path)}
                title={node.name}
              >
                {/* Empty: the triangle is drawn in CSS off aria-expanded, so the
                    open/closed state has exactly one source. */}
                <span className="wiki-caret" aria-hidden="true" />
                <span className="wiki-name">{node.name}</span>
              </button>
              {expanded.has(node.path) && (
                <WikiRows
                  nodes={node.children}
                  depth={depth + 1}
                  expanded={expanded}
                  onToggle={onToggle}
                  selected={selected}
                  onSelect={onSelect}
                  fixtures={fixtures}
                />
              )}
            </Fragment>
          )
        ) : (
          <button
            key={node.path}
            // `traced` only alongside `is-active`, matching `.chat-row` — the
            // shimmering edge marks the one thing that's alive, not every row.
            className={`wiki-row${selected === node.path ? ' is-active traced' : ''}`}
            onClick={() => void onSelect(node)}
            title={node.name}
          >
            <span className="wiki-name">{node.name}</span>
            {/* Empty on an untracked file, which is the honest thing to show:
                there is no date, rather than a date nobody can read. A project
                shows its state's age rather than the directory's, because the
                state file is the part that goes stale. */}
            <span className="wiki-when">
              {commitAge(
                node.type === 'dir'
                  ? (childNamed(node, 'state.md')?.last_commit ?? null)
                  : node.last_commit,
              )}
            </span>
          </button>
        ),
      )}
    </>
  );
}



/**
 * The projects a new conversation can be opened with, grouped as the registry
 * groups them.
 *
 * Every card is a toggle and none of them has to be pressed. That is the whole
 * change from the picker this replaces: choosing was a step you had to finish
 * before there was anywhere to type, and the one thing you chose was then read
 * — by the session itself — as a wall it never was. A choice here is context the
 * conversation starts with, and the first one is where it opens.
 */
/** What someone typed is a place on this machine rather than something to clone. */
function looksLikePath(source: string): boolean {
  return source.startsWith('/') || source.startsWith('~') || source.startsWith('.');
}

/**
 * The name a repo gets if nobody types one.
 *
 * A copy of the runner's rule, and only ever shown as placeholder text — the
 * runner derives its own and is the one that decides. Worth the duplication
 * because the alternative is a field whose default you cannot see until after
 * the clone, and the name is what the whole picker then calls it.
 */
function derivedName(source: string): string {
  const tail = source.replace(/\/+$/, '').split('/').pop()?.split(':').pop() ?? '';
  return tail.replace(/\.git$/, '').toLowerCase();
}

/**
 * Add a repo to the list, without opening a terminal.
 *
 * Adding a project used to mean editing a JSON file on the box over ssh and
 * restarting the runner — which is the one job this console could not be used
 * for, since you need a project before a conversation is much use. So it is a
 * card in the same set as the projects it adds to, rather than a settings
 * screen somewhere else.
 *
 * One field for the repo, because on a phone a mode switch above two fields is
 * worse than a rule: a `/` or `~` at the front means a directory already on the
 * box, and anything else is a url to clone. The rest are optional and the form
 * says which of them changes what.
 */
function AddRepo({
  projects,
  onAdd,
}: {
  /** Existing project names, offered as completions so groups don't fork on a typo. */
  projects: string[];
  onAdd: (spec: {
    url?: string;
    path?: string;
    name?: string;
    project?: string;
    description?: string;
  }) => Promise<Workspace>;
}) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState('');
  const [name, setName] = useState('');
  const [project, setProject] = useState('');
  const [description, setDescription] = useState('');
  const [adding, setAdding] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const form = useRef<HTMLFormElement>(null);

  /**
   * Scroll the opened form clear of the composer.
   *
   * It is the last thing in a list that already reaches the bottom, and the
   * composer floats over that bottom — so opening it four fields tall put the
   * Add button underneath, where it could be neither seen nor pressed.
   *
   * The scroller is moved to its end rather than the form being asked to bring
   * itself into view. `scrollIntoView` aligns the form's own bottom edge with
   * the bottom of the scrollport, which is exactly where the composer is; what
   * clears it is `.stream`'s bottom padding, and that is only on screen once
   * the list is scrolled all the way down.
   */
  useEffect(() => {
    if (!open) return;
    const scroller = form.current?.closest('.stream');
    if (scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
    else form.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [open]);

  if (!open) {
    return (
      <button type="button" className="ws ws-add" onClick={() => setOpen(true)}>
        <span className="ws-name">Add a repo</span>
        <span className="ws-desc">Clone one from GitHub, or point at a directory already here.</span>
      </button>
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const from = source.trim();
    if (!from || adding) return;
    setAdding(true);
    setProblem(null);
    try {
      await onAdd({
        [looksLikePath(from) ? 'path' : 'url']: from,
        name: name.trim() || undefined,
        project: project.trim() || undefined,
        description: description.trim() || undefined,
      });
      setOpen(false);
      setSource('');
      setName('');
      setProject('');
      setDescription('');
    } catch (err) {
      setProblem((err as Error).message);
    } finally {
      setAdding(false);
    }
  }

  return (
    <form ref={form} className="ws ws-add is-open" onSubmit={submit}>
      <label className="ws-field">
        <span>Repo</span>
        <input
          value={source}
          onChange={e => setSource(e.target.value)}
          placeholder="git@github.com:fian-team/… or /home/rowan/…"
          autoFocus
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          disabled={adding}
        />
      </label>
      <label className="ws-field">
        <span>Name</span>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={derivedName(source.trim()) || 'from the url'}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          disabled={adding}
        />
      </label>
      <label className="ws-field">
        <span>Project</span>
        <input
          value={project}
          onChange={e => setProject(e.target.value)}
          placeholder="groups it in this list"
          list="ws-projects"
          disabled={adding}
        />
        <datalist id="ws-projects">
          {projects.map(p => (
            <option key={p} value={p} />
          ))}
        </datalist>
      </label>
      <label className="ws-field">
        <span>About</span>
        <input
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="one line — sessions are told this"
          disabled={adding}
        />
      </label>
      {/* Said before the press, not after: a clone is somebody's whole repo over
          somebody's network, and this window sits still until git is done. */}
      <p className="ws-add-note">
        {adding
          ? 'Cloning. Large repos take a while — this stays put until it lands.'
          : 'Private repos work if this machine’s key can already read them.'}
      </p>
      {problem && <p className="ws-add-problem">{problem}</p>}
      <div className="ws-add-actions">
        <button type="button" className="btn-quiet" onClick={() => setOpen(false)} disabled={adding}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={adding || !source.trim()}>
          {adding ? 'Adding…' : 'Add'}
        </button>
      </div>
    </form>
  );
}

export function ProjectPicker({
  workspaces,
  chosen,
  onToggle,
  onAdd,
  loading,
}: {
  workspaces: Workspace[];
  /** Names, in the order they were ticked. The first is the working directory. */
  chosen: string[];
  onToggle: (ws: Workspace) => void;
  /** Absent leaves the list read-only, which is what it was before this existed. */
  onAdd?: (spec: {
    url?: string;
    path?: string;
    name?: string;
    project?: string;
    description?: string;
  }) => Promise<Workspace>;
  loading: boolean;
}) {
  return (
    <nav className="ws-list">
      {byProject(workspaces).map(([project, repos]) => (
        <div className="ws-group" key={project}>
          <div className="section-label">{project}</div>
          {repos.map(ws => {
            const at = chosen.indexOf(ws.name);
            return (
              <button
                key={ws.name}
                type="button"
                className={`ws${at >= 0 ? ' is-active' : ''}`}
                // A toggle, so `aria-pressed` rather than a link's or a menu
                // item's semantics. Nothing here navigates and nothing here is
                // one-of-several, which is what would have made it a radio.
                aria-pressed={at >= 0}
                onClick={() => onToggle(ws)}
              >
                {/* Only on the pane, where the cards flow in one grid and the
                    group headings are gone — see `.ws-project`. In the drawer the
                    heading above the card is already saying this. */}
                <span className="ws-project">{project}</span>
                <span className="ws-name">{ws.name}</span>
                <span className="ws-desc">{ws.description}</span>
                {/* Which one the session actually opens in, said only when
                    there is more than one and it could therefore be in doubt.
                    It decides where `git` acts and which CLAUDE.md is found, so
                    it is worth a word on the card rather than in a paragraph
                    nobody reads twice. */}
                {at === 0 && chosen.length > 1 && <span className="ws-lead">starts here</span>}
              </button>
            );
          })}
        </div>
      ))}
      {loading && <div className="muted">Loading…</div>}
      {/* Last, in its own group rather than inside one: it belongs to no project
          and putting it in the final one would read as belonging to that. */}
      {onAdd && !loading && (
        <div className="ws-group">
          <AddRepo
            projects={byProject(workspaces).map(([project]) => project)}
            onAdd={onAdd}
          />
        </div>
      )}
    </nav>
  );
}
